import {
    afterRenderEffect,
    computed,
    ElementRef,
    type Injector,
    type Signal,
    signal,
    NgZone,
} from '@angular/core';
import { assertInjector } from './assert-injector';

export interface ElementBreakpoints<Name extends string> {
    readonly width: Signal<number | undefined>;
    atLeast(name: Name): Signal<boolean | undefined>;
    lessThan(name: Name): Signal<boolean | undefined>;
}

/** Observe a host or reactive child target's content-box inline size in CSS pixels. */
export function injectElementBreakpoints<Name extends string>(options: {
    readonly thresholds: Readonly<Record<Name, number>>;
    readonly target?:
        | ElementRef<HTMLElement>
        | Signal<ElementRef<HTMLElement> | undefined>;
    readonly injector?: Injector;
}): ElementBreakpoints<Name> {
    const injector = assertInjector(injectElementBreakpoints, options.injector);
    const thresholds = new Map<string, number>(
        Object.entries(options.thresholds)
    );
    for (const [name, threshold] of thresholds) {
        if (!Number.isFinite(threshold) || threshold < 0) {
            throw new Error(
                `Invalid element breakpoint ${name}: expected a finite nonnegative width`
            );
        }
    }
    const target =
        options.target ?? injector.get<ElementRef<HTMLElement>>(ElementRef);
    const width = signal<number | undefined>(undefined);
    const comparisons = new Map<string, Signal<boolean | undefined>>();
    const zone = injector.get(NgZone);

    afterRenderEffect(
        {
            read: (onCleanup) => {
                const element = (
                    typeof target === 'function' ? target() : target
                )?.nativeElement;
                width.set(undefined);
                if (!element || typeof ResizeObserver === 'undefined') {
                    return;
                }
                let active = true;
                const observer = zone.runOutsideAngular(() => {
                    const observer = new ResizeObserver((entries) => {
                        if (!active) {
                            return;
                        }
                        const entry = entries.find(
                            (entry) => entry.target === element
                        );
                        if (entry) {
                            width.set(
                                entry.contentBoxSize[0]?.inlineSize ??
                                    entry.contentRect.width
                            );
                        }
                    });
                    observer.observe(element, { box: 'content-box' });
                    return observer;
                });
                onCleanup(() => {
                    active = false;
                    observer.disconnect();
                });
            },
        },
        { injector }
    );

    function compare(
        name: Name,
        operator: 'atLeast' | 'lessThan'
    ): Signal<boolean | undefined> {
        const threshold = thresholds.get(name);
        if (threshold === undefined) {
            throw new Error(`Unknown element breakpoint: ${name}`);
        }
        const key = `${operator}:${name}`;
        let comparison = comparisons.get(key);
        if (!comparison) {
            comparison = computed(() => {
                const measured = width();
                if (measured === undefined) {
                    return undefined;
                }
                return operator === 'atLeast'
                    ? measured >= threshold
                    : measured < threshold;
            });
            comparisons.set(key, comparison);
        }
        return comparison;
    }

    return {
        width: width.asReadonly(),
        atLeast: (name) => compare(name, 'atLeast'),
        lessThan: (name) => compare(name, 'lessThan'),
    };
}
